<template>
  <div>
    <!-- eslint-disable vue/require-component-is -->
    <section class="acc-sect-wrapper" :class="{ 'margin-bottom-m': notification.component || notification.message }">
      <Notification
        v-if="notification.component || notification.message"
        :key="notification.key"
        :severity="notification.type"
        :closable="notification.closeButton"
      >
        <component :is="notification.component" v-if="notification.component" v-bind="notification.props" />
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
        <div v-else @click="handleAddNewCardClick" v-html="notification.message"></div>
      </Notification>
    </section>
    <section v-show="!showGlobalError" id="account-sect">
      <LoyaltyBanner v-if="killswitches?.newLoyaltyAndCreditBannerEnabled" />
      <LoyaltyHeader v-else />
      <div ref="accountVerificationObserver" class="cls-account-verification-container">
        <component
          :is="accountVerificationMessage"
          v-if="accountVerificationMessage && shouldShowAccountVerification"
          :is-returning-customer="!route.query.verifyAccount && !profileStore.profile?.accountVerified"
        />
      </div>
      <OrderHistory />
      <CreditCards />
      <OfferPromos />
      <div class="show-for-large">
        <section ref="wishlistObserver" class="cls-lazy-section">
          <AtomicUiSkeleton v-if="!wishlist" :style="{ height: 'clamp(200px, 25vw, 320px)' }" />
          <component :is="wishlist" v-else />
        </section>
      </div>
      <section v-if="brand === 'mcom'" ref="subscriptionsObserver" class="margin-bottom-l cls-lazy-section">
        <AtomicUiSkeleton v-if="!subscriptions" :style="{ height: 'clamp(120px, 15vw, 180px)' }" />
        <component :is="subscriptions" v-else />
      </section>
      <LeftNav class="hide-for-large padding-right-s margin-bottom-l" :no-accordion="brand === 'bcom'" />
      <div ref="prosObserver" class="cls-lazy-section">
        <AtomicUiSkeleton v-if="!pros" :style="{ height: 'clamp(100px, 12vw, 150px)' }" />
        <component :is="pros" v-else />
      </div>
      <section ref="medalliaSurveyObserver" class="cls-lazy-section">
        <AtomicUiSkeleton v-if="!medalliaSurvey && killswitches?.medalliaSurveyEnabled" :style="{ height: 'clamp(80px, 10vw, 120px)' }" />
        <component :is="medalliaSurvey" v-else-if="medalliaSurvey && killswitches?.medalliaSurveyEnabled" />
      </section>
      <ModalOverlay :id="ui.modalOverlayStore.id" :title="ui.modalOverlayStore.title" :width="ui.modalOverlayStore.width">
        <template #body>
          <component :is="ui.modalOverlayStore.component" v-if="ui.modalOverlayStore.component" v-bind="ui.modalOverlayStore.props" />
        </template>
      </ModalOverlay>
      <Slideout
        :id="slideoutStack[0]?.id"
        :title="slideoutStack[0]?.title"
        :backable="slideoutStack[0]?.backable"
        handle-top-layer="show"
        @back-slideout="backSlide"
        @close-slideout="resetSlideout"
      >
        <component :is="slideoutStack[0]?.component" v-if="slideoutStack[0]?.component" v-bind="slideoutStack[0]?.props" />
      </Slideout>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineAsyncComponent, markRaw, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { Cookie } from '@vue-util/cookie';
import Slideout from '@atomic-ui/slideout/Slideout';
import ModalOverlay from '@atomic-ui/modalOverlay/ModalOverlay';
import Notification from '@atomic-ui/notification/Notification';
import LeftNav from '@nuxt-common/left-nav/app/components/LeftNav';
import { fireTag } from '@vue-util/tagManager';
import { brand, WALLET, INBOX } from 'config';
import LoyaltyBanner from '@/components/loyaltyHeader/LoyaltyBanner';
import LoyaltyHeader from '@/components/loyaltyHeader/LoyaltyHeader';
import OrderHistory from '@/components/orderHistory/OrderHistory';
import CreditCards from '@/components/payments/CreditCards';
import OfferPromos from '@/components/offerPromos/OfferPromos';
import AtomicUiSkeleton from '@atomic-ui/skeleton/Skeleton';
import { lazyLoadWhenInViewport } from '@/utils/childViewsRenderer';
import { useDashboardStore } from '@/stores/useDashboardStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useProfileStore } from '@/stores/useProfileStore';
import { useWalletStore } from '@/stores/useWalletStore';
import useSlideout from '@/composables/useSlideout';
import { useUserSessionId } from '@/composables/useUserSessionId';
import useRoktSDKPlus from '@nuxt-common/site-monetization/app/composables/useRoktSDKPlus';

const envProps = useEnvPropsStore();

const route = useRoute();
const router = useRouter();
const dashboardStore = useDashboardStore();
const profileStore = useProfileStore();
const walletStore = useWalletStore();
const { showSlideout, closeSlideout } = useSlideout();
const { userSessionIdInit } = useUserSessionId();
const isBCOM = import.meta.env.VITE_ENTRY_POINT === 'bcom';
const { notification, killswitches, globalError: showGlobalError, ui, slideoutStack, signedInError } = storeToRefs(dashboardStore);

let timerId = null;

onUnmounted(() => {
  if (timerId) {
    clearTimeout(timerId);
  }
});

const { killSwitches = {} } = storeToRefs(envProps);
dashboardStore.setKillswitches(killSwitches);

if (typeof window !== 'undefined') {
  const { makeOneCCGetPublicKeyCall, canRetryGetPublicKeyCall } = killSwitches.value;
  // Temporary workaround to expose kill switch values to the encrypted-creditcard module to determine whether
  // the public key API should be called once with retry support or multiple times based on kill switch configuration.
  window.makeOneCCGetPublicKeyCall = makeOneCCGetPublicKeyCall;
  window.canRetryGetPublicKeyCall = canRetryGetPublicKeyCall;
}

const shouldShowAccountVerification = computed(() => killSwitches.value?.accountVerificationEnabled);

const wishlist = ref(null);
const pros = ref(null);
const subscriptions = ref(null);
const medalliaSurvey = ref(null);
const accountVerificationMessage = ref(null);

const wishlistObserver = ref(null);
const prosObserver = ref(null);
const subscriptionsObserver = ref(null);
const medalliaSurveyObserver = ref(null);
const accountVerificationObserver = ref(null);

useHead({
  title: 'My Account Dashboard',
});

watch(signedInError, () => {
  if (signedInError.value) {
    Cookie.set('SignedIn', '0');
    window.location.href = '/account/signin';
  }
});

/*
 * Opens the credit card slideout automatically after account creation.
 * Context (`origin: 'createAccount'`) is passed because the user has
 * already been verified by `create-account-component` during signup,
 * allowing the child component to safely bypass reCAPTCHA.
 *
 * NOTE: Do not reuse this helper for standard "add card" actions,
 *       as those must require standard reCAPTCHA.
 */
const openAddCreditCardSlideout = () => {
  const slideoutTitle = isBCOM ? 'Manage your loyallist membership' : "Your account has been created! Now add your Macy's credit card";

  showSlideout(INBOX.addCardToWallet.addCardSlideoutId, slideoutTitle, import('@/components/payments/AddCardSlideoutWrapper'), true, {
    cardAction: 'add',
    callback: async (response) => {
      const status = response?.walletTender?.msg || 'error';
      if (status === 'success') {
        router.replace({ query: { ...route.query, loyaltyAccount: 'true' } });
        await walletStore.fetchCreditCards();
        await dashboardStore.setGlobalNotification({
          type: 'success',
          closeButton: true,
          message: WALLET.cardSaveSuccess,
        });
        fireTag('link', {
          site_action: 'add credit card complete',
          action_location: 'create account',
        });
        await dashboardStore.fetchRewardsSummary().catch(() => {});
      } else {
        const message = response?.walletTender?.error?.message || '';
        await dashboardStore.setGlobalNotification({
          type: 'error',
          closeButton: true,
          message,
        });
        router.replace({ query: { ...route.query, loyaltyAccount: 'false' } });
      }
      dashboardStore.resetSlideout();
      closeSlideout(INBOX.addCardToWallet.addCardSlideoutId);
    },
    isOnlyMacysOption: true,
    origin: 'createAccount',
    isBCOM,
    tokenizationIframeSource: envProps.tokenizationIframeSource,
    tokenizationPciIframeSrc: envProps.tokenizationPciIframeSrc,
    deviceType: envProps.deviceType,
    isCheckboxDisable: true,
    userAddress: profileStore.profile?.shippingAddress,
    userEmail: profileStore.profile?.email,
    beautyBoxSubscription: { paymentCardID: null },
  });
  userSessionIdInit();

  fireTag('link', {
    site_action: 'add credit card start',
    action_location: 'create account',
  });
};

const newProfileCreated = () => {
  if (route.query.profileCreated || (route.query.starRewardsEnrollment && route.query.profileCreated)) {
    dashboardStore.setGlobalNotification({
      type: 'success',
      closeButton: true,
      component: markRaw(defineAsyncComponent(() => import('@/components/messages/UserCreationMessage'))),
      props: {
        starRewardsEnrollment: route.query.starRewardsEnrollment === 'true',
        loyaltyAutoEnrollment: route.query.loyaltyEnrolled === 'true', // Same as starrewards for MCOM
        profileCreated: route.query.profileCreated === 'true',
        loyaltyAccountFetched: route.query.loyaltyAccountFetched === 'true',
      },
    });
  }
};

const checkHasCreditCard = () => {
  if (route.query.hasCreditCard === 'Yes') {
    timerId = setTimeout(() => {
      openAddCreditCardSlideout();
      const { hasCreditCard, ...remainingQuery } = route.query;
      router.replace({ query: remainingQuery });
    }, 500);
  }
};

const handleAddNewCardClick = (e) => {
  const addNewCardClicked = e.target.closest('.add-a-new-card');
  if (addNewCardClicked) {
    document.getElementById('add-a-card').click();
  }
};

const backSlide = () => {
  if (slideoutStack.value[0]?.id === INBOX.addCardToWallet.addCardSlideoutId && slideoutStack.value[0]?.props?.origin === 'createAccount') {
    router.replace({ query: { ...route.query, loyaltyAccount: 'false' } });
  }
  slideoutStack.value.shift();
};

const resetSlideout = () => {
  if (slideoutStack.value[0]?.id === INBOX.addCardToWallet.addCardSlideoutId && slideoutStack.value[0]?.props?.origin === 'createAccount') {
    router.replace({ query: { ...route.query, loyaltyAccount: 'false' } });
  }
  dashboardStore.resetSlideout();
};

// Defer non-critical trackers and monetization scripts to keep main thread free for LCP paint
const deferNonCritical = () => {
  fireTag('view', {
    page_name: 'home',
    page_type: 'my account',
  });
  useRoktSDKPlus({
    brand,
    isRoktSDKPlusEnabled: killswitches.value?.isRoktSDKPlusEnabled,
    screenName: 'account',
  });
};

onMounted(async () => {
  await dashboardStore.fetchUserSummary();
  newProfileCreated();
  checkHasCreditCard();
  lazyLoadWhenInViewport(() => wishlistObserver.value).then(() => {
    wishlist.value = markRaw(defineAsyncComponent(() => import('@/components/wishlist/Wishlist')));
  });
  lazyLoadWhenInViewport(() => prosObserver.value).then(() => {
    pros.value = markRaw(defineAsyncComponent(() => import('@/components/ProsCollection')));
  });
  lazyLoadWhenInViewport(() => subscriptionsObserver.value).then(() => {
    subscriptions.value = markRaw(defineAsyncComponent(() => import('@/components/ManageSubscriptions')));
  });
  if (killswitches.value?.medalliaSurveyEnabled) {
    lazyLoadWhenInViewport(() => medalliaSurveyObserver.value).then(() => {
      medalliaSurvey.value = markRaw(defineAsyncComponent(() => import('@/components/MedalliaSurvey')));
    });
  }
  lazyLoadWhenInViewport(() => accountVerificationObserver.value).then(() => {
    accountVerificationMessage.value = markRaw(defineAsyncComponent(() => import('@/components/messages/AccountVerificationMessage')));
  });

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(deferNonCritical);
  } else {
    setTimeout(deferNonCritical, 0);
  }
});
</script>

<style lang="scss" scoped>
.p-message {
  margin: 0;
}

.cls-lazy-section {
  contain: layout style;
  content-visibility: auto;
  contain-intrinsic-size: auto 200px;
}

.cls-account-verification-container {
  contain: layout style;
}
</style>
